/**
 * Adapter nowego API raportów: /reports/work-history, /reports/imei-changes,
 * /reports/band-history.
 *
 * Koperta odpowiedzi jest ta sama co w /alarms, więc `readPage`, `toQueryParams`
 * i `toApiInstant` bierzemy wprost z `alarms-api` — tutaj siedzi tylko to, czego
 * tamten adapter nie zna: kształt wierszy raportów i nazwy kolumn sortowania.
 *
 * Czyste funkcje, bez zależności od Angulara — testują się bez TestBed.
 */

import { firstDefined, toIsoDate } from '../alarms-api';

/** Wspólne filtry wszystkich trzech list. `from`/`to`: chwila w RFC 3339 (`toApiInstant`), `limit` 1–200. */
export interface ReportsQuery {
  /** Fraza wyszukiwania — backend przeszukuje kolumny tekstowe danej listy. */
  q?: string;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}

export interface WorkHistoryQuery extends ReportsQuery {
  /**
   * Zakładki kategorii (błąd 349), od 2026-08-26 także KILKA naraz — slugi sklejone
   * przecinkami, łączone przez OR. Tym jedzie panel filtrów.
   *
   * Pusty napis = brak filtra; `toQueryParams` wyrzuca go z zapytania. Nieznany slug
   * to 400 na całe żądanie, więc wysyłamy wyłącznie klucze z WORK_HISTORY_FILTER_GROUPS.
   */
  category?: string;
  /**
   * Kod akcji, np. `login`, `edit`, `transferTC`. JEDEN, bez przecinków, i łączony
   * z `category` przez AND — dlatego panel go nie używa: para w rodzaju
   * `login` + `medicines` nie ma wspólnych wierszy.
   */
  action?: string;
  /** date | user | action | parameter | description */
  sort?: string;
  /** asc | desc */
  dir?: string;
}

export interface ImeiChangesQuery extends ReportsQuery {
  /** Filtr trafia w OBA numery — stary i nowy. */
  imei?: string;
}

export interface BandHistoryQuery extends ReportsQuery {
  imei?: string;
}

/**
 * Kolumna tabeli → wartość `sort` przyjmowana przez API. Nagłówek „Szczegóły"
 * pokazuje pole `parameter`, więc nazwa kolumny w UI i w API się różnią —
 * bez tej mapy backend odpowiedziałby 400 na nieznaną wartość filtra.
 */
const WORK_HISTORY_SORT_COLUMNS: { [column: string]: string } = {
  date: 'date',
  user: 'user',
  action: 'action',
  details: 'parameter',
  parameter: 'parameter',
  description: 'description'
};

/** `null` dla kolumny, której API nie umie sortować — wtedy nie wysyłamy filtra wcale. */
export function toWorkHistorySort(column: string): string {
  return WORK_HISTORY_SORT_COLUMNS[column] || null;
}


/**
 * Kategorie wpisów historii pracy — „zakładki" z błędu 349. `slug` to wartość parametru
 * `category` przyjmowana przez API i jest KONTRAKTEM backendu: nie tłumaczymy go i nie
 * zmieniamy bez uzgodnienia po obu stronach. Nieznany slug backend odrzuca przez 400,
 * więc literówka wywali się głośno, a nie udając pustą listę.
 *
 * `labelKey` wskazuje tłumaczenia obecne w komplecie 20 plików i18n (sprawdzone plik
 * po pliku) — zakładka bez nazwy w którymś języku pokazałaby tam surowy klucz.
 *
 * Lista pokrywa KOMPLET 16 kategorii przyjmowanych przez API. Trzy z nich bywają puste
 * i to nie jest usterka panelu: kody z rodzin `forms` i `calendar` zapisują wyłącznie
 * endpointy mobilne, a `announcements` ma wiersze tylko na koncie administratora.
 * Pusta zakładka mówi „nie ma czego pokazać"; brak zakładki ukrywałby filtr, do którego
 * użytkownik nie ma jak dotrzeć z żadnej innej strony.
 *
 * `icon` to klasa Font Awesome 5 (zestaw `fas`, wpięty w index.html). Tam, gdzie
 * kategoria ma swój odpowiednik w badge'ach tabeli, glif jest CELOWO ten sam co
 * w getActionIcon() — chip i wiersze, które przepuszcza, mają wyglądać jak jedna
 * rodzina. Reszta się wyłamuje z podanych powodów: `patient_reports` bierze `fa-file-medical`,
 * bo `fa-file-alt` z tabeli zajmuje już chip „Raport całościowy" w tym samym rzędzie;
 * `measurements` bierze neutralne `fa-chart-line`, bo w tabeli pomiar ma inny glif dla
 * wody, a inny dla ciśnienia; `sessions`, `forms` i `announcements` nie mają w tabeli
 * żadnego odpowiednika i biorą ikonę najbliższą nazwie.
 */
export const WORK_HISTORY_CATEGORIES: Array<{ slug: string, labelKey: string, icon: string }> = [
  { slug: 'visits', labelKey: 'BACKEND.CARETAKER-SCHEDULE-VISIT', icon: 'fa-briefcase' },
  { slug: 'medicines', labelKey: 'USER.MEDICINES', icon: 'fa-pills' },
  { slug: 'diseases', labelKey: 'USER.HEALTH-CONDITIONS', icon: 'fa-heartbeat' },
  { slug: 'patient_reports', labelKey: 'SETTINGS.ACTION_REPORT', icon: 'fa-file-medical' },
  { slug: 'reach', labelKey: 'WORK-HISTORY.CATEGORY-REACH', icon: 'fa-phone-alt' },
  { slug: 'measurements', labelKey: 'USER.MEASUREMENTS', icon: 'fa-chart-line' },
  { slug: 'alarms', labelKey: 'COMMON.ALARMS', icon: 'fa-bell' },
  { slug: 'alarm_zones', labelKey: 'WORK-HISTORY.ALARM-ZONE', icon: 'fa-map-marker-alt' },
  { slug: 'alarm_phones', labelKey: 'BACKEND.ALARM_PHONES', icon: 'fa-address-book' },
  { slug: 'restrictions', labelKey: 'WORK-HISTORY.RESTRICTIONS', icon: 'fa-user-lock' },
  { slug: 'caregivers', labelKey: 'HEADER.CAREGIVERS', icon: 'fa-user-nurse' },
  { slug: 'sessions', labelKey: 'VOIP.SESSIONS_TAB', icon: 'fa-headset' },
  { slug: 'mfa', labelKey: 'MFA.TITLE', icon: 'fa-shield-alt' },
  { slug: 'calendar', labelKey: 'BUTTONS.CALENDAR', icon: 'fa-calendar-alt' },
  { slug: 'forms', labelKey: 'WORK-HISTORY.CATEGORY-FORMS', icon: 'fa-clipboard-list' },
  { slug: 'announcements', labelKey: 'WORK-HISTORY.CATEGORY-ANNOUNCEMENTS', icon: 'fa-bullhorn' },
  // user-scoped (Etap 12/13): odpięcie/przypięcie opaski, magazyn, zmiana IMEI
  { slug: 'bands', labelKey: 'BACKEND.NAVIGATION-BANDS', icon: 'fa-exchange-alt' }
];

/**
 * Pozycja panelu filtrów: jeden checkbox.
 *
 * Bez ikony — wiersz to kwadrat wyboru i nazwa, nic więcej. Glif obok każdej pozycji
 * niczego tu nie rozstrzyga (nazwa już to robi), a dwadzieścia pięć glifów w wąskim
 * popoverze zamienia listę w mozaikę.
 */
export interface WorkHistoryFilterItem {
  /** Slug w parametrze `category` — KONTRAKT backendu, nie tłumaczymy go. */
  key: string;
  labelKey: string;
}

/** Sekcja panelu filtrów: nagłówek i jego checkboxy. */
export interface WorkHistoryFilterGroup {
  key: string;
  labelKey: string;
  items: WorkHistoryFilterItem[];
}

/**
 * Słownik panelu filtrów historii pracy — 26 kluczy w 7 sekcjach.
 *
 * `key` to slug parametru `category`, a jego DEFINICJĄ jest zbiór kodów `action_code`
 * po stronie backendu. Front tej mapy nie zna i celowo nie chce znać: kodów jest 124
 * i przybywa ich z każdym modułem, więc lista trzymana tutaj po cichu gubiłaby wpisy
 * do czasu naszego wdrożenia — dokładnie to, czego uczy błąd 349. Kontrakt spisany
 * w `docs/2026-08-26-work-history-panel-filtrow-kontrakt.md`.
 *
 * Klucze są ROZŁĄCZNE — żaden kod nie należy do dwóch pozycji. Bez tego checkbox
 * kłamie: kategoria `sessions` obejmuje `login`, `logout` i `pass`, więc odznaczenie
 * „Logowania" zostawiałoby logowania na liście, wciągnięte przez zaznaczone „Sesje".
 * Dlatego `sessions` znaczy tu WYŁĄCZNIE wyrzucenie z sesji, a rodzina alarmów
 * rozchodzi się na zgłoszenie (`alarm`) i obsługę (`alarm_handling`).
 *
 * UWAGA: te slugi nie znaczą tego samego co jednobrzmiące wartości `action`.
 * `category=login` to `{login, login_mfa_required}`, a `action=login` to dokładnie
 * `login`; `category=delete` nie obejmuje `delete_caregiver`, a rodzina `action=delete`
 * tak. Panel używa wyłącznie `category` i nie miesza tych dwóch słowników.
 *
 * SLUGI CZEKAJĄCE NA BACKEND (stan 2026-08-26): `login`, `logout`, `password`, `add`,
 * `edit`, `delete`, `transfer_tc`, `alarm`, `alarm_handling`, `all_report`. Na nieznany
 * slug API odpowiada 400 na CAŁE żądanie — prośba o przecięcie mapy `CATEGORIES` leży
 * w dokumencie kontraktu, sekcja 3.
 *
 * Slugi są zawsze w `snake_case` — pilnuje tego test. To nie jest kosmetyka: klucze tej
 * mapy bywają myślone z KODAMI AKCJI, które backend pisze w wierszach camelCase-em
 * (`transferTC`). Slug `transfer_tc` i kod `transferTC` to to samo zdarzenie w dwóch
 * różnych słownikach, zbieżne nazwy — nie mieszać.
 *
 * `labelKey` wskazuje tłumaczenia obecne w komplecie 20 plików i18n — pozycja bez nazwy
 * w którymś języku pokazałaby tam surowy klucz.
 */
export const WORK_HISTORY_FILTER_GROUPS: WorkHistoryFilterGroup[] = [
  {
    key: 'account_security',
    labelKey: 'SETTINGS.WH_FILTER.GROUP_ACCOUNT_SECURITY',
    items: [
      { key: 'login', labelKey: 'SETTINGS.ACTION_LOGIN' },
      { key: 'logout', labelKey: 'SETTINGS.ACTION_LOGOUT' },
      { key: 'password', labelKey: 'SETTINGS.ACTION_PASS_CHANGE' },
      { key: 'mfa', labelKey: 'MFA.TITLE' },
      { key: 'sessions', labelKey: 'SETTINGS.WH_FILTER.KEY_SESSIONS' }
    ]
  },
  {
    key: 'user_management',
    labelKey: 'SETTINGS.WH_FILTER.GROUP_USER_MANAGEMENT',
    items: [
      { key: 'add', labelKey: 'SETTINGS.ACTION_ADD' },
      { key: 'edit', labelKey: 'SETTINGS.ACTION_EDIT' },
      { key: 'delete', labelKey: 'SETTINGS.ACTION_DELETE' },
      { key: 'transfer_tc', labelKey: 'SETTINGS.ACTION_TRANSFER_TC' }
    ]
  },
  {
    key: 'bands',
    labelKey: 'SETTINGS.WH_FILTER.GROUP_BANDS',
    items: [
      { key: 'change_imei', labelKey: 'SETTINGS.ACTION_CHANGE_IMEI' }
    ]
  },
  {
    key: 'medical_data',
    labelKey: 'SETTINGS.WH_FILTER.GROUP_MEDICAL_DATA',
    items: [
      { key: 'measurements', labelKey: 'USER.MEASUREMENTS' },
      { key: 'medicines', labelKey: 'USER.MEDICINES' },
      { key: 'diseases', labelKey: 'USER.HEALTH-CONDITIONS' },
      { key: 'visits', labelKey: 'BACKEND.CARETAKER-SCHEDULE-VISIT' },
      { key: 'restrictions', labelKey: 'WORK-HISTORY.RESTRICTIONS' },
      { key: 'alarm_phones', labelKey: 'BACKEND.ALARM_PHONES' },
      { key: 'reach', labelKey: 'WORK-HISTORY.CATEGORY-REACH' }
    ]
  },
  {
    key: 'alarms',
    labelKey: 'SETTINGS.WH_FILTER.GROUP_ALARMS',
    items: [
      { key: 'alarm', labelKey: 'SETTINGS.WH_FILTER.KEY_ALARM' },
      { key: 'alarm_handling', labelKey: 'SETTINGS.WH_FILTER.KEY_ALARM_HANDLING' },
      { key: 'alarm_zones', labelKey: 'WORK-HISTORY.ALARM-ZONE' }
    ]
  },
  {
    key: 'reports',
    labelKey: 'SETTINGS.WH_FILTER.GROUP_REPORTS',
    items: [
      { key: 'all_report', labelKey: 'SETTINGS.ACTION_ALL_REPORT' },
      { key: 'patient_reports', labelKey: 'SETTINGS.ACTION_REPORT' }
    ]
  },
  {
    key: 'organization',
    labelKey: 'SETTINGS.WH_FILTER.GROUP_ORGANIZATION',
    items: [
      { key: 'caregivers', labelKey: 'HEADER.CAREGIVERS' },
      { key: 'calendar', labelKey: 'BUTTONS.CALENDAR' },
      { key: 'forms', labelKey: 'WORK-HISTORY.CATEGORY-FORMS' },
      { key: 'announcements', labelKey: 'WORK-HISTORY.CATEGORY-ANNOUNCEMENTS' }
    ]
  }
];
/**
 * Kody akcji, których tłumaczenie jest obiektem, a nie zdaniem: kanał logowania
 * i typ raportu zbiorczego. Tylko przy nich wolno dokleić `parameter` do klucza.
 * Sprawdzone we wszystkich 20 plikach assets/i18n — reszta sekcji WORK-HISTORY to
 * płaskie napisy.
 */
const NESTED_ACTIONS = ['login', 'allReport'];

/**
 * Wpis historii pracy → klucz tłumaczenia jego opisu.
 *
 * Doklejenie parametru do akcji o płaskim tłumaczeniu daje klucz, którego w plikach
 * i18n nie ma, a ngx-translate oddaje wtedy sam klucz — w tabeli stało dosłownie
 * `WORK-HISTORY.caretaker_start.860123456789` (błąd 349). Backend wkłada w `parameter`
 * IMEI albo kanał przy niemal każdym wpisie, więc dotyczyło to całych rodzin naraz:
 * wizyt opiekuna terenowego, Reach, leków, MFA i ocen.
 *
 * Rodziny opisywane własnym kluczem (strefy, blokady, leki) rozpoznaje wywołujący —
 * ta funkcja składa klucz dla pozostałych.
 */
export function toWorkHistoryActionKey(action: string, parameter?: string): string {
  const key = 'WORK-HISTORY.' + action;
  return parameter && NESTED_ACTIONS.indexOf(action) !== -1 ? key + '.' + parameter : key;
}

/**
 * Wpis historii pracy o koncie opiekuna → klucz tłumaczenia.
 */
const CAREGIVER_ACCOUNT_LABEL_KEYS: { [action: string]: string } = {
  add_caregiver: 'WORK-HISTORY.caregiver_add',
  create_caregiver: 'WORK-HISTORY.caregiver_add',
  edit_caregiver: 'WORK-HISTORY.caregiver_edit',
  update_caregiver: 'WORK-HISTORY.caregiver_edit',
  delete_caregiver: 'WORK-HISTORY.caregiver_delete',
  remove_caregiver: 'WORK-HISTORY.caregiver_delete'
};

/**
 * `null` = wpis nie dotyczy konta opiekuna; wtedy wywołujący zostaje przy swoim
 * dotychczasowym zachowaniu (w starej tabeli: klucz `WORK-HISTORY.<kod>` wprost).
 */
export function toCaregiverActionKey(action: string): string {
  return CAREGIVER_ACCOUNT_LABEL_KEYS[(action || '').toString().toLowerCase()] || null;
}

/**
 * Wiersz z /reports/work-history: id, date, name, user_id, action, parameter, description.
 *
 * `date` przechodzi przez `toIsoDate`, który czyta RFC 3339 (v2) i dla pewności
 * także unix w sekundach. Resztę pól czytamy w obu konwencjach
 * (camelCase i snake_case) i zostawiamy oryginalne pola nietknięte.
 */
export function normalizeWorkHistoryRow(raw: any): any {
  if (!raw) {
    return raw;
  }

  return {
    ...raw,
    id: firstDefined(raw.id, raw.historyId, raw.history_id),
    date: toIsoDate(firstDefined(raw.date, raw.createdAt, raw.created_at)),
    name: firstDefined(raw.name, raw.userName, raw.user_name) || '',
    userId: firstDefined(raw.userId, raw.user_id),
    action: firstDefined(raw.action) || '',
    parameter: firstDefined(raw.parameter, raw.param),
    description: firstDefined(raw.description, raw.desc)
  };
}

/**
 * Pole `description` wiersza historii pracy → tekst komórki „Opis".
 *
 * Backend zapisuje tam JSON: klucz na pole, a przy edycji parę `{old, new}`.
 * Cokolwiek innego (zwykły tekst, nie-JSON) przepuszczamy bez zmian.
 *
 * Pary bez zmiany pomijamy. Formularz pacjenta wysyła przy zapisie cały swój
 * stan, więc backend zapisuje parę dla KAŻDEGO pola — po przestawieniu samej
 * głośności opis liczył czterdzieści linii `pole: 45 → 45` i jedyna faktyczna
 * zmiana w nich ginęła.
 *
 * `describeTerm` jest opcjonalne: dostaje nazwę pola i każdą tekstową albo logiczną wartość,
 * a oddaje ludzki opis albo `null`, gdy takiego nie zna. Bez niego nazwy pól zostają takie,
 * jakie przyszły z bazy. `locale` nazywa dzień godzin pracy; bez niego zostaje liczba.
 */
export function formatWorkHistoryDescription(description: any, describeTerm?: (text: string) => string, locale?: string): string {
  if (description === null || description === undefined || description === '') {
    return '';
  }
  if (typeof description !== 'string') {
    return String(description);
  }
  let parsed: any;
  try {
    parsed = JSON.parse(description);
  } catch (e) {
    return description;
  }
  if (parsed === null || typeof parsed !== 'object') {
    return description;
  }
  const lines: string[] = [];
  visibleDescriptionKeys(parsed).forEach(key => {
    const value = key === 'privilege' ? toPrivilegeChange(parsed[key]) : parsed[key];
    const name = describeValue(key, describeTerm);
    if (isChangePair(value)) {
      if (isSameDescriptionValue(value.old, value.new)) {
        return;
      }
      lines.push(`${name}: ${formatDescriptionValue(key, value.old, describeTerm, locale)} → ${formatDescriptionValue(key, value.new, describeTerm, locale)}`);
    } else if (isPatientList(key, value)) {
      lines.push(`${name}:`, ...value.map(patient => `• ${describePatient(patient)}`));
    } else {
      lines.push(`${name}: ${formatDescriptionValue(key, value, describeTerm, locale)}`);
    }
  });
  return lines.join('\n');
}

const HIDDEN_DESCRIPTION_KEYS = ['id', 'deviceId', 'userId', 'caregiver', 'caregiverId', 'project',
  'statusOfCreate', 'dayLabel', 'patient_id', 'patientId', 'requestedUserId', 'usersBandId', 'handledBy'];

/** Część tras api2 wpisuje tu id z bazy, reszta IMEI albo pacjentów z nazwiskiem; zliczamy tylko id. */
const PATIENT_LIST_KEYS = ['assigned', 'removed'];

/** Bez przeliczania strefy: data urodzenia zapisana o północy przesunęłaby się o dzień. */
const DATABASE_DATETIME = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}:\d{2})(?:[+-]\d{2}:\d{2}|Z)$/;

function visibleDescriptionKeys(value: object): string[] {
  return Object.keys(value).filter(key => HIDDEN_DESCRIPTION_KEYS.indexOf(key) === -1
    && !isAlarmPhoneRecordIdPair(key, value[key])
    && !repeatsNewImei(key, value));
}

/** Przy zmianie IMEI api2 zapisuje nowy numer dwa razy: w `new_imei` i w `patientImei`. */
function repeatsNewImei(key: string, description: object): boolean {
  return key === 'patientImei' && 'new_imei' in description;
}

/** `privilege` to cały zestaw flag opiekuna, także tych bez nazwy w interfejsie; zostają zdjęte i dodane. */
function toPrivilegeChange(value: any): any {
  if (!isChangePair(value)) {
    return value;
  }
  const codes = (text: any): string[] => typeof text === 'string' ? text.split(';').filter(Boolean) : [];
  const before = codes(value.old);
  const after = codes(value.new);
  return {
    old: before.filter(code => after.indexOf(code) === -1),
    new: after.filter(code => before.indexOf(code) === -1)
  };
}

function isPatientList(key: string, value: any): boolean {
  return PATIENT_LIST_KEYS.indexOf(key) !== -1 && Array.isArray(value) && value.length > 0 && !value.every(isDatabaseId);
}

function describePatient(patient: any): string {
  if (patient && typeof patient === 'object') {
    return patient.patientName ? `${patient.patientName} (${patient.patientImei})` : String(patient.patientImei);
  }
  return String(patient);
}

/** IMEI ma zawsze 15 cyfr; krótsza liczba, także zapisana tekstem, to id wiersza z bazy. */
function isDatabaseId(item: any): boolean {
  return typeof item === 'number' || (typeof item === 'string' && /^\d{1,14}$/.test(item));
}

/** Liczby w `alarmPhones` to id rekordów telefonów z błędnego diffu api2, a nie numery. */
function isAlarmPhoneRecordIdPair(key: string, value: any): boolean {
  return key === 'alarmPhones' && isChangePair(value)
    && (typeof value.old === 'number' || typeof value.new === 'number');
}

function isChangePair(value: any): boolean {
  return !!value && typeof value === 'object' && ('old' in value || 'new' in value);
}

/**
 * Brak wartości i wartość pusta wyglądają w tabeli tak samo.
 *
 * Lista wymaga własnej gałęzi: `String(['a','b'])` skleja elementy bez spacji,
 * a `String([])` daje pusty tekst, więc zdjęcie ostatniej blokady wyglądało jak
 * urwany wiersz („restrictions:  → …") — błąd 423.
 */
function formatDescriptionValue(key: string, value: any, describeTerm?: (text: string) => string, locale?: string): string {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  if (Array.isArray(value)) {
    if (value.length && PATIENT_LIST_KEYS.indexOf(key) !== -1 && value.every(isDatabaseId)) {
      return String(value.length);
    }
    const items = value
      .map(item => formatDescriptionValue(key, item, describeTerm, locale))
      .filter(item => item !== '—');
    return items.length ? items.join(', ') : '—';
  }
  if (typeof value === 'object') {
    if ('startHour' in value) {
      return value.enabled === false ? '—' : `${value.startHour || '—'}–${value.endHour || '—'}`;
    }
    return '(' + visibleDescriptionKeys(value)
      .map(field => `${describeValue(field, describeTerm)}: ${formatDescriptionValue(field, value[field], describeTerm, locale)}`)
      .join(', ') + ')';
  }
  if (key === 'day' && locale && typeof value === 'number' && value >= 0 && value <= 6) {
    // 0 = niedziela, jak `format('w')` w api2; 1 stycznia 2023 wypadł w niedzielę.
    return new Date(2023, 0, 1 + value).toLocaleDateString(locale, { weekday: 'long' });
  }
  if (key === 'immoTime' && typeof value === 'number') {
    // api2 mnoży minuty z formularza przez 60 (`Band::setImmoTime`).
    return `${value / 60} min`;
  }
  if (key === 'immoThreshold' && typeof value === 'number' && value >= 8000) {
    // api2 zapisuje suwak 1–20 jako 8000 + 600 × n (`Band::setImmoThreshold`), a 0 bez zmian.
    return String((value - 8000) / 600);
  }
  if (key === 'fallCanceledAt' && typeof value === 'string' && DATABASE_DATETIME.test(value)) {
    const canceledAt = new Date(value);
    const pad = (part: number) => String(part).padStart(2, '0');
    return `${canceledAt.getFullYear()}-${pad(canceledAt.getMonth() + 1)}-${pad(canceledAt.getDate())} `
      + `${pad(canceledAt.getHours())}:${pad(canceledAt.getMinutes())}:${pad(canceledAt.getSeconds())}`;
  }
  if (typeof value === 'string' && DATABASE_DATETIME.test(value)) {
    return value.replace(DATABASE_DATETIME, '$1 $2');
  }
  return describeValue(value, describeTerm);
}

/**
 * Nazwa pola albo wartość tekstowa czy logiczna → opis od wywołującego, a gdy go nie zna,
 * to samo, co przyszło z bazy. Liczb słownik nie dotyka: opisuje pojęcia, a nie liczby.
 */
function describeValue(value: any, describeTerm?: (text: string) => string): string {
  if (describeTerm && (typeof value === 'string' || typeof value === 'boolean')) {
    return describeTerm(String(value)) || String(value);
  }
  return String(value);
}

/**
 * Czy para stara/nowa niesie jakąkolwiek zmianę.
 *
 * Porównanie musi znosić typy, bo backend oddaje to samo ustawienie raz jako
 * `true`, raz jako `1` (`isHr: true → 1`), a pole, którego nigdy nie było, raz
 * jako `null`, raz jako pusty tekst. Ostrożnie w drugą stronę: 0 wobec braku
 * wartości to już zmiana i zostaje w opisie. `false` wobec braku — nie: formularze
 * api2 zamieniają odznaczone pole na null, a nieustawioną chorobę na false.
 */
function isSameDescriptionValue(oldValue: any, newValue: any): boolean {
  const oldText = comparableDescriptionValue(oldValue);
  const newText = comparableDescriptionValue(newValue);
  return oldText === newText
    || (oldValue === false && newText === '')
    || (newValue === false && oldText === '');
}

function comparableDescriptionValue(value: any): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  // Pusta lista znaczy to samo, co brak wartości — żadnych blokad. Pola, którego
  // nigdy nie było, backend oddaje jako null, a puste dziś jako `[]`, więc bez tego
  // opis niósłby wiersz „— → —": szum mówiący, że nic się nie zmieniło.
  if (Array.isArray(value) && !value.length) {
    return '';
  }
  if (value === true) {
    return '1';
  }
  if (value === false) {
    return '0';
  }
  // String() dałby „[object Object]" dla każdego obiektu, więc każda zmiana
  // strefy alarmowej wyglądałaby jak brak zmiany.
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
}

/**
 * Wiersz z /reports/imei-changes.
 *
 * Stara trasa pakowała samą zmianę w element `[0]` tablicy, a imię i nazwisko
 * osoby zmieniającej trzymała na tym samym obiekcie obok — stąd `data[0].old_imei`
 * i `data.firstName` w dotychczasowych szablonach. Kształtu pojedynczego elementu
 * nowej trasy backend nie zadeklarował, więc czytamy jedno i drugie: zagnieżdżenie
 * `[0]`, snake_case i camelCase. Szablony dostają zawsze te same cztery pola.
 */
export function normalizeImeiChangeRow(raw: any): any {
  if (!raw) {
    return raw;
  }

  const change = raw[0] || raw;

  const firstName = firstDefined(raw.firstName, raw.first_name, change.firstName, change.first_name);
  const lastName = firstDefined(raw.lastName, raw.last_name, change.lastName, change.last_name);
  const fullName = `${firstName || ''} ${lastName || ''}`.trim();

  return {
    ...raw,
    changedAt: toIsoDate(firstDefined(change.changed_at, change.changedAt, change.date, raw.date)),
    oldImei: firstDefined(change.old_imei, change.oldImei, raw.old_imei, raw.oldImei),
    newImei: firstDefined(change.new_imei, change.newImei, raw.new_imei, raw.newImei),
    changedBy: fullName || firstDefined(raw.changedBy, raw.changed_by, change.changedBy, change.changed_by) || ''
  };
}
